import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { UserRecord } from '../../models/auth.model';

@Component({
  selector: 'app-sign-in',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './sign-in.component.html',
})
export class SignInComponent {
  private readonly phonePattern = /^\+?[0-9()\-\s]{7,20}$/;
  private readonly strongPasswordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;

  email = '';
  password = '';

  loading = false;
  authMessage = '';
  authError = '';

  currentUser: UserRecord | null = null;
  users: UserRecord[] = [];
  usersLoading = false;
  usersError = '';

  newUserFirstName = '';
  newUserLastName = '';
  newUserEmail = '';
  newUserPhone = '';
  newUserPassword = '';

  editingUserId: number | null = null;
  editFirstName = '';
  editLastName = '';
  editEmail = '';
  editPhone = '';
  editPassword = '';

  constructor(private api: ApiService) {}

  private isValidPhone(phone: string): boolean {
    return !phone || this.phonePattern.test(phone);
  }

  private isStrongPassword(password: string): boolean {
    return this.strongPasswordPattern.test(password);
  }

  signIn(): void {
    this.authMessage = '';
    this.authError = '';

    if (!this.email.trim() || !this.password.trim()) {
      this.authError = 'Email and password are required.';
      return;
    }

    this.loading = true;
    this.api.signIn({ email: this.email.trim(), password: this.password }).subscribe({
      next: (res) => {
        this.loading = false;
        this.currentUser = res.user;
        this.authMessage = res.message || 'Signed in successfully.';
        this.loadUsers();
      },
      error: (err) => {
        this.loading = false;
        this.authError = err?.error?.detail || 'Sign in failed.';
      },
    });
  }

  loadUsers(): void {
    this.usersLoading = true;
    this.usersError = '';
    this.api.getUsers().subscribe({
      next: (rows) => {
        this.users = rows;
        this.usersLoading = false;
      },
      error: (err) => {
        this.usersLoading = false;
        this.usersError = err?.error?.detail || 'Failed to load users.';
      },
    });
  }

  createUser(): void {
    this.usersError = '';

    const trimmedPhone = this.newUserPhone.trim();
    const trimmedPassword = this.newUserPassword.trim();

    if (!this.newUserFirstName.trim() || !this.newUserLastName.trim() || !this.newUserEmail.trim() || !trimmedPassword) {
      this.usersError = 'First name, last name, email and password are required.';
      return;
    }

    if (!this.isValidPhone(trimmedPhone)) {
      this.usersError = 'Phone must be 7-20 characters and contain only digits, spaces, parentheses, hyphens, or a leading +.';
      return;
    }

    if (!this.isStrongPassword(trimmedPassword)) {
      this.usersError = 'Password must be at least 8 characters and include uppercase, lowercase, number, and special character.';
      return;
    }

    this.api
      .createUser({
        first_name: this.newUserFirstName.trim(),
        last_name: this.newUserLastName.trim(),
        email: this.newUserEmail.trim(),
        phone: trimmedPhone || null,
        password: trimmedPassword,
      })
      .subscribe({
        next: () => {
          this.newUserFirstName = '';
          this.newUserLastName = '';
          this.newUserEmail = '';
          this.newUserPhone = '';
          this.newUserPassword = '';
          this.loadUsers();
        },
        error: (err) => {
          this.usersError = err?.error?.detail || 'Failed to create user.';
        },
      });
  }

  startEdit(user: UserRecord): void {
    const id = this.getUserId(user);
    if (id === null) {
      this.usersError = 'Cannot edit user without numeric id.';
      return;
    }

    this.editingUserId = id;
    this.editFirstName = typeof user.first_name === 'string' ? user.first_name : '';
    this.editLastName = typeof user.last_name === 'string' ? user.last_name : '';
    this.editEmail = typeof user.email === 'string' ? user.email : '';
    this.editPhone = typeof user.phone === 'string' ? user.phone : '';
    this.editPassword = '';
  }

  cancelEdit(): void {
    this.editingUserId = null;
    this.editFirstName = '';
    this.editLastName = '';
    this.editEmail = '';
    this.editPhone = '';
    this.editPassword = '';
  }

  saveEdit(): void {
    if (this.editingUserId === null) {
      return;
    }

    const trimmedPhone = this.editPhone.trim();
    const trimmedPassword = this.editPassword.trim();

    const payload: { first_name?: string; last_name?: string; email?: string; phone?: string | null; password?: string } = {};

    if (this.editFirstName.trim()) {
      payload.first_name = this.editFirstName.trim();
    }
    if (this.editLastName.trim()) {
      payload.last_name = this.editLastName.trim();
    }
    if (this.editEmail.trim()) {
      payload.email = this.editEmail.trim();
    }
    if (trimmedPhone) {
      if (!this.isValidPhone(trimmedPhone)) {
        this.usersError = 'Phone must be 7-20 characters and contain only digits, spaces, parentheses, hyphens, or a leading +.';
        return;
      }
      payload.phone = trimmedPhone;
    }
    if (trimmedPassword) {
      if (!this.isStrongPassword(trimmedPassword)) {
        this.usersError = 'Password must be at least 8 characters and include uppercase, lowercase, number, and special character.';
        return;
      }
      payload.password = trimmedPassword;
    }

    if (Object.keys(payload).length === 0) {
      this.usersError = 'Provide at least one value to update.';
      return;
    }

    this.api.updateUser(this.editingUserId, payload).subscribe({
      next: () => {
        this.cancelEdit();
        this.loadUsers();
      },
      error: (err) => {
        this.usersError = err?.error?.detail || 'Failed to update user.';
      },
    });
  }

  deleteUser(user: UserRecord): void {
    const id = this.getUserId(user);
    if (id === null) {
      this.usersError = 'Cannot delete user without numeric id.';
      return;
    }

    this.api.deleteUser(id).subscribe({
      next: () => this.loadUsers(),
      error: (err) => {
        this.usersError = err?.error?.detail || 'Failed to delete user.';
      },
    });
  }

  isEditing(user: UserRecord): boolean {
    return this.getUserId(user) === this.editingUserId;
  }

  getDisplayName(user: UserRecord): string {
    const firstName = user.first_name;
    const lastName = user.last_name;
    const left = typeof firstName === 'string' ? firstName.trim() : '';
    const right = typeof lastName === 'string' ? lastName.trim() : '';

    if (left || right) {
      return `${left} ${right}`.trim();
    }

    return '';
  }

  getUserId(user: UserRecord): number | null {
    if (typeof user.id === 'number') {
      return user.id;
    }

    return null;
  }
}
